import { PersonContactReview } from '@/components/people/person-contact-review'
import { usePendingPersonReview } from '../usePendingPersonReview'
import { isPersonMatchTerm, PersonMatchQuery, type TPersonMatch } from '@schemas'
import { Fragment, useDeferredValue, useEffect, useId, useRef, useState } from 'react'
import { LookupResults } from '@/components/lookup-results'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient, subscribeToPersonAccessDenials } from '@/lib/apiClient'
import { parseApiErrorMessage, parseApiErrorPayload } from '@/lib/api-errors'
import type { ReactNode } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import type { TExhibitorWithEntries, TPerson } from '@schemas'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { ClubSelector } from '@/components/entries/ClubSelector'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Badge } from '@/components/ui/badge'
import { useShowPeopleSearch, useShowPeopleSuggestions, useCreateExhibitor } from '@/api/hooks'
import { normalizeOptional } from '@/features/intake/utils'
import { useCheckInStepperContext } from '../CheckInStepper'

export type EntrantReadySource = 'new' | 'existing-person' | 'existing-entrant'
export type EntrantReadyContext = {
  source: EntrantReadySource
}

const newEntrantSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().optional().or(z.literal('')).or(z.null()),
  email: z.string().email('Enter a valid email').optional().or(z.literal('')).or(z.null()),
  phone: z.string().optional().or(z.literal('')).or(z.null()),
  affiliation: z.string().optional().or(z.literal('')).or(z.null()),
})

type NewEntrantFormValues = z.infer<typeof newEntrantSchema>

type EntrantStepProps = {
  currentShowId: string | null
  currentShowOrgId: string | null
  permissionLoading: boolean
  canManageExhibitors: boolean
  exhibitorRolesLabel: string
  exhibitorPermissionDisabled: boolean
  exhibitorPermissionReason?: string
  mergePermissionDisabled: boolean
  mergePermissionReason?: string
  exhibitors: TExhibitorWithEntries[]
  exhibitorsByPersonId: Map<string, TExhibitorWithEntries>
  exhibitorsLoading: boolean
  onOpenMergePerson: (person: Pick<TPerson, 'id' | 'name'>) => void
  onEntrantReady?: (entrant: TExhibitorWithEntries, context: EntrantReadyContext) => void
  isOnline: boolean
  clubsEnabled: boolean
  grandChampionEligible: boolean
  onGrandChampionEligibilityChange: (next: boolean, options?: { exhibitorId?: string }) => void
  disableGrandChampionToggle?: boolean
  grandChampionToggleTitle?: string
}

type Mode = 'create' | 'search'

const joinNameParts = (first?: string | null, last?: string | null) =>
  [first?.trim(), last?.trim()].filter(Boolean).join(' ')

// The suggestion lookup skips a field the matching endpoint would reject.
const isSuggestionTerm = (value: string) => PersonMatchQuery.safeParse({ q: value }).success

export function EntrantStep({
  currentShowId,
  currentShowOrgId,
  permissionLoading,
  canManageExhibitors,
  exhibitorRolesLabel,
  exhibitorPermissionDisabled,
  exhibitorPermissionReason,
  mergePermissionDisabled,
  mergePermissionReason,
  exhibitors,
  exhibitorsByPersonId,
  exhibitorsLoading,
  onOpenMergePerson,
  onEntrantReady,
  isOnline,
  clubsEnabled,
  grandChampionEligible,
  onGrandChampionEligibilityChange,
  disableGrandChampionToggle = false,
  grandChampionToggleTitle,
}: EntrantStepProps) {
  const [mode, setMode] = useState<Mode>('create')
  const [searchTerm, setSearchTerm] = useState('')
  const deferredSearch = useDeferredValue(searchTerm)
  const trimmedSearch = deferredSearch.trim()
  const [newEntrantClubId, setNewEntrantClubId] = useState<string | null>(null)
  const createMatchesId = useId()
  const searchResultsId = useId()
  const queryClient = useQueryClient()
  const [usingPersonId, setUsingPersonId] = useState<string | null>(null)
  const [personUseError, setPersonUseError] = useState<{
    message: string
    expired: boolean
  } | null>(null)
  // The Use person request in flight. Clearing it makes that request's late responses stale.
  const personUseAttempt = useRef<symbol | null>(null)

  useEffect(() => {
    setUsingPersonId(null)
    setPersonUseError(null)
    const unsubscribe = subscribeToPersonAccessDenials(() => {
      const pending = personUseAttempt.current !== null
      personUseAttempt.current = null
      setUsingPersonId(null)
      if (pending) {
        setPersonUseError({
          message:
            'Your access changed. Sign in or check your show assignment before using this person.',
          expired: false,
        })
      }
    })
    return () => {
      unsubscribe()
      personUseAttempt.current = null
    }
  }, [currentShowId, isOnline, exhibitorPermissionDisabled, permissionLoading])

  const stepper = useCheckInStepperContext()

  const { accessMessage, reviewMatch, setReviewMatch, editPersonId, setEditPersonId } =
    usePendingPersonReview(currentShowId, setSearchTerm)
  const createExhibitor = useCreateExhibitor(currentShowId)

  const form = useForm<NewEntrantFormValues>({
    resolver: zodResolver(newEntrantSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      affiliation: '',
    },
  })

  const formValues = form.watch()
  const emailQuery = normalizeOptional(formValues.email ?? undefined)
  const nameQuery = joinNameParts(formValues.firstName, formValues.lastName)
  const deferredNameQuery = useDeferredValue(nameQuery)
  const deferredEmailQuery = useDeferredValue(emailQuery ?? '')
  const createLookupKey = JSON.stringify([currentShowId, deferredNameQuery, deferredEmailQuery])
  const matchingUnavailableMessage = !currentShowId
    ? 'Matching could not be checked. Select a show to search existing people.'
    : permissionLoading
      ? 'Matching could not be checked while permissions are loading.'
      : !isOnline
        ? 'Matching could not be checked while offline. Your entered details are retained.'
        : exhibitorPermissionDisabled
          ? `Matching could not be checked. ${exhibitorPermissionReason ?? 'You do not have permission to search people for this show.'}`
          : undefined
  const matchingEnabled = matchingUnavailableMessage === undefined

  const createLikelySearch = useShowPeopleSuggestions(
    currentShowId,
    deferredNameQuery,
    deferredEmailQuery,
    mode === 'create' && matchingEnabled
  )

  const searchResults = useShowPeopleSearch(
    currentShowId,
    trimmedSearch,
    mode === 'search' && matchingEnabled
  )

  const likelyMatches = createLikelySearch.data?.people ?? []

  const renderPersonDetails = (
    person: TPersonMatch,
    entrant?: TExhibitorWithEntries
  ): ReactNode => {
    const parts: ReactNode[] = []
    if (person.email) parts.push(person.email)
    if (person.phone) parts.push(person.phone)
    const location = [person.displayCity, person.displayState].filter(Boolean).join(', ')
    if (location) parts.push(location)
    if (entrant?.entrantCode) {
      parts.push(
        <span key="entrant-code">
          Entrant code <span className="font-mono">{entrant.entrantCode}</span>
        </span>
      )
    }

    if (parts.length === 0) {
      return 'No contact info'
    }

    return parts.map((part, index) => (
      <Fragment key={`detail-${index}`}>
        {index > 0 && <span className="px-1 text-muted-foreground/80">•</span>}
        {part}
      </Fragment>
    ))
  }

  const handleResetTransientState = () => {
    setMode('create')
    setSearchTerm('')
    form.reset()
    setNewEntrantClubId(null)
  }

  const previousStepId = useRef(stepper.activeStepId)
  useEffect(() => {
    if (stepper.activeStepId === 'entrant' && previousStepId.current !== 'entrant') {
      handleResetTransientState()
    }
    previousStepId.current = stepper.activeStepId
  }, [stepper.activeStepId])

  const handleEntrantReady = (entrant: TExhibitorWithEntries, context: EntrantReadyContext) => {
    stepper.setSelectedEntrant(entrant)
    if (onEntrantReady) {
      onEntrantReady(entrant, context)
    } else {
      stepper.goToStep('entries')
    }
  }

  const handleCreateSubmit = (values: NewEntrantFormValues) => {
    if (personUseAttempt.current !== null) return
    if (!currentShowOrgId) {
      toast.error('Select a show before creating a walk-in exhibitor.')
      return
    }
    if (exhibitorPermissionDisabled) {
      toast.error(exhibitorPermissionReason ?? 'Creating entrants requires additional permissions.')
      return
    }

    const entrantName = joinNameParts(values.firstName, values.lastName)

    createExhibitor.mutate(
      {
        orgId: currentShowOrgId,
        name: entrantName,
        firstName: normalizeOptional(values.firstName),
        lastName: normalizeOptional(values.lastName),
        email: normalizeOptional(values.email ?? undefined),
        phone: normalizeOptional(values.phone ?? undefined),
        ...(clubsEnabled
          ? { affiliation: normalizeOptional(values.affiliation ?? undefined) }
          : {}),
      },
      {
        onSuccess: exhibitor => {
          const shaped: TExhibitorWithEntries = {
            ...exhibitor,
            entries: [],
            person: undefined,
            entrantCode: exhibitor.entrantCode,
          }
          toast.success(`Exhibitor ${exhibitor.name} created`)
          form.reset()
          setNewEntrantClubId(null)
          handleEntrantReady(shaped, { source: 'new' })
          if (!grandChampionEligible) {
            onGrandChampionEligibilityChange(false, { exhibitorId: exhibitor.id })
          }
        },
        onError: error => {
          toast.error(error.message)
        },
      }
    )
  }

  const handleSelectPerson = async (person: TPersonMatch) => {
    if (!matchingEnabled || personUseAttempt.current !== null || createExhibitor.isPending) return
    setReviewMatch(person)
  }

  const handleUsePerson = async (person: TPersonMatch) => {
    if (
      !person.id ||
      !currentShowId ||
      !matchingEnabled ||
      personUseAttempt.current !== null ||
      createExhibitor.isPending
    )
      return
    const attempt = Symbol('use person')
    personUseAttempt.current = attempt
    const isCurrent = () => personUseAttempt.current === attempt
    setUsingPersonId(person.id)
    setPersonUseError(null)
    try {
      const selection = await apiClient.selectShowPerson(currentShowId, person.matchProof)
      if (!isCurrent()) return
      const { exhibitor } = await apiClient.createShowEntrant(currentShowId, {
        personId: selection.person.id,
        selectionProof: selection.selectionProof,
      })
      // The entrant is registered even when this attempt was cancelled in the meantime.
      void queryClient.invalidateQueries({ queryKey: ['exhibitors'] })
      if (!isCurrent()) return
      handleEntrantReady(exhibitor, { source: 'existing-person' })
    } catch (error) {
      if (!isCurrent()) return
      const expired = parseApiErrorPayload(error)?.code === 'selection_expired'
      setPersonUseError({
        expired,
        message: expired
          ? 'This match has expired. Refresh matches, then choose Use person again.'
          : parseApiErrorMessage(error, 'Unable to use this person. Try Use person again.'),
      })
    } finally {
      if (isCurrent()) {
        personUseAttempt.current = null
        setUsingPersonId(null)
      }
    }
  }

  const personActionPending = usingPersonId !== null || createExhibitor.isPending
  const renderPersonActions = (person: TPersonMatch) => (
    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
      <Button
        type="button"
        size="sm"
        className="min-w-[8rem]"
        onClick={() => void handleUsePerson(person)}
        disabled={!person.id || personActionPending}
      >
        {usingPersonId === person.id ? 'Using person…' : 'Use person'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="min-w-[8rem]"
        onClick={() => void handleSelectPerson(person)}
        disabled={!person.id || personActionPending}
      >
        Review person
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => onOpenMergePerson({ id: person.id, name: person.name })}
        disabled={!person.id || mergePermissionDisabled || personActionPending}
        title={mergePermissionReason}
      >
        Merge
      </Button>
    </div>
  )

  // Rendered beside the visible results, so a failure shows where Use person was pressed.
  const personUseAlert = personUseError && (
    <div className="space-y-2">
      <p role="alert" className="text-sm text-destructive">
        {personUseError.message}
      </p>
      {personUseError.expired && (
        <Button
          type="button"
          variant="outline"
          disabled={!matchingEnabled || createLikelySearch.isFetching || searchResults.isFetching}
          onClick={() => {
            setPersonUseError(null)
            // Refetches the visible lookup; a hidden or unsearchable one is only marked stale.
            void queryClient.invalidateQueries({ queryKey: ['show-person-match', currentShowId] })
            // This button leaves with the error, so move focus to the refreshed results.
            document.getElementById(mode === 'create' ? createMatchesId : searchResultsId)?.focus()
          }}
        >
          Refresh matches
        </Button>
      )}
    </div>
  )

  return (
    <div className="space-y-4" data-testid="entrant-step-content">
      {accessMessage && <p role="alert">{accessMessage}</p>}
      {currentShowId && (reviewMatch || editPersonId) && (
        <PersonContactReview
          key={`${currentShowId}:${reviewMatch?.id ?? editPersonId}`}
          showId={currentShowId}
          match={reviewMatch ?? undefined}
          personId={editPersonId ?? undefined}
          isOnline={isOnline}
          onClose={() => {
            setReviewMatch(null)
            setEditPersonId(null)
          }}
          onUse={
            reviewMatch
              ? entrant => handleEntrantReady(entrant, { source: 'existing-person' })
              : undefined
          }
        />
      )}
      {stepper.selectedEntrant?.personId && (
        <Button
          type="button"
          variant="outline"
          disabled={!isOnline || exhibitorPermissionDisabled || personActionPending}
          onClick={() => setEditPersonId(stepper.selectedEntrant!.personId!)}
        >
          Edit contact details
        </Button>
      )}
      <Tabs value={mode} onValueChange={value => setMode(value as Mode)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="create">Add New Entrant</TabsTrigger>
            <TabsTrigger value="search">Find Existing Person</TabsTrigger>
          </TabsList>
          {!permissionLoading && !canManageExhibitors && (
            <p className="text-xs text-muted-foreground">
              Creating entrants requires {exhibitorRolesLabel}.
            </p>
          )}
        </div>

        <TabsContent value="create" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Walk-in entrant</CardTitle>
              <CardDescription>
                Start a new entrant profile. We’ll check for likely matches as you type.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Form {...form}>
                <form
                  aria-label="New entrant details"
                  aria-describedby={`${createMatchesId}-status`}
                  onSubmit={form.handleSubmit(handleCreateSubmit)}
                  className="grid gap-3 md:grid-cols-2"
                >
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First name</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value ?? ''} placeholder="Jane" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last name (optional)</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value ?? ''} placeholder="Doe" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email (optional)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ''}
                            placeholder="name@example.com"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone (optional)</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value ?? ''} placeholder="555-123-4567" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {clubsEnabled ? (
                    <FormField
                      control={form.control}
                      name="affiliation"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Club (optional)</FormLabel>
                          <FormControl>
                            <ClubSelector
                              organizationId={currentShowOrgId ?? null}
                              hasOrganization={Boolean(currentShowOrgId)}
                              isOnline={isOnline}
                              value={field.value ?? ''}
                              onValueChange={next => {
                                field.onChange(next)
                                setNewEntrantClubId(null)
                              }}
                              selectedClubId={newEntrantClubId}
                              onClubIdChange={setNewEntrantClubId}
                              disabled={
                                permissionLoading ||
                                exhibitorPermissionDisabled ||
                                !canManageExhibitors
                              }
                              placeholder="Search clubs…"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}

                  <div className="md:col-span-2">
                    <Button
                      type="submit"
                      disabled={personActionPending || exhibitorPermissionDisabled}
                      title={exhibitorPermissionReason}
                    >
                      {createExhibitor.isPending ? 'Creating…' : 'Create entrant'}
                    </Button>
                  </div>
                </form>
              </Form>
              <label className="flex items-start justify-between gap-3 rounded-md border border-dashed border-border/60 px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Not eligible for Grand Champion</p>
                  <p className="text-xs text-muted-foreground">
                    When checked, this entrant is excluded from Grand Champion awards and reports.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border border-input"
                  checked={!grandChampionEligible}
                  onChange={event => onGrandChampionEligibilityChange(!event.target.checked)}
                  disabled={disableGrandChampionToggle}
                  aria-label="Not eligible for Grand Champion"
                  title={grandChampionToggleTitle}
                  data-testid="new-entrant-grand-champion-toggle"
                />
              </label>

              {personUseAlert}
              <LookupResults
                id={createMatchesId}
                label="Likely matching people"
                query={createLikelySearch}
                lookupKey={createLookupKey}
                unavailableMessage={
                  matchingUnavailableMessage ??
                  (!isSuggestionTerm(nameQuery) && !isSuggestionTerm(emailQuery ?? '')
                    ? 'Enter at least three letters or digits in a name or email to check for likely matches.'
                    : undefined)
                }
                waitingForQuery={
                  nameQuery !== deferredNameQuery || (emailQuery ?? '') !== deferredEmailQuery
                }
                loadingMessage="Checking for likely matching people…"
                forbiddenMessage="Matching could not be checked. You do not have access to search people for this show."
                errorMessage="Matching could not be checked. Your entered details are retained. Retry to check for likely matches."
                successMessage={
                  likelyMatches.length === 0
                    ? 'No likely matches found.'
                    : `${likelyMatches.length} likely ${likelyMatches.length === 1 ? 'match' : 'matches'} found.`
                }
              >
                {createLikelySearch.data?.refineSearch && (
                  <p>More matches exist. Add a last name or refine the email.</p>
                )}
                {likelyMatches.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="font-medium">We found existing people that might match:</p>
                    <ul className="mt-2 space-y-2">
                      {likelyMatches.map((person, index) => {
                        const entrant = person.id ? exhibitorsByPersonId.get(person.id) : undefined
                        const isCurrentEntrant = Boolean(entrant)
                        const secondaryDetails = renderPersonDetails(person, entrant)
                        return (
                          <li
                            key={person.id ?? `likely-person-${index}`}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white px-3 py-2"
                          >
                            <div className="min-w-0 flex-1 basis-64 break-words">
                              <p className="font-medium">{person.name}</p>
                              <p className="text-xs text-muted-foreground">{secondaryDetails}</p>
                            </div>
                            {isCurrentEntrant && (
                              <Badge variant="outline">Current show entrant</Badge>
                            )}
                            {renderPersonActions(person)}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
              </LookupResults>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="search" className="mt-0">
          <Card>
            <CardHeader>
              <CardTitle>Find existing person</CardTitle>
              <CardDescription>
                Search the existing people and link them to this show as an entrant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                aria-label="Search existing people"
                aria-controls={searchResultsId}
                aria-describedby={`${searchResultsId}-status`}
                placeholder="Search by name, email, or phone"
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
              />

              {personUseAlert}
              <LookupResults
                id={searchResultsId}
                label="Existing person search results"
                query={searchResults}
                lookupKey={trimmedSearch}
                unavailableMessage={
                  matchingUnavailableMessage ??
                  (!isPersonMatchTerm(searchTerm.trim())
                    ? 'Type at least three letters or digits to search the existing people.'
                    : undefined)
                }
                waitingForQuery={searchTerm.trim() !== trimmedSearch}
                loadingMessage="Searching people…"
                forbiddenMessage="Matching could not be checked. You do not have access to search people for this show."
                errorMessage="Matching could not be checked. Your search is retained. Retry to search people."
                successMessage={
                  searchResults.data?.people.length
                    ? `${searchResults.data.people.length} ${searchResults.data.people.length === 1 ? 'person' : 'people'} found.`
                    : 'No matching people'
                }
              >
                {searchResults.data?.refineSearch && <p>More matches exist. Refine your search.</p>}
                {Boolean(searchResults.data?.people.length) && (
                  <ul className="space-y-2">
                    {searchResults.data?.people.map((person, index) => {
                      const entrant = person.id ? exhibitorsByPersonId.get(person.id) : undefined
                      const isCurrentEntrant = Boolean(entrant)
                      const secondaryDetails = renderPersonDetails(person, entrant)
                      return (
                        <li
                          key={person.id ?? `people-result-${index}`}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm"
                        >
                          <div className="min-w-0 flex-1 basis-64 space-y-1 break-words">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium">{person.name}</p>
                              {person.ahstcMember && <Badge variant="secondary">AHSTC</Badge>}
                              {isCurrentEntrant && (
                                <Badge variant="outline">Current show entrant</Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">{secondaryDetails}</p>
                          </div>
                          {renderPersonActions(person)}
                        </li>
                      )
                    })}
                  </ul>
                )}
              </LookupResults>

              <div className="space-y-2">
                <p className="text-sm font-medium">Unlinked entrants (advanced)</p>
                <div className="max-h-48 overflow-y-auto rounded-md border">
                  {exhibitorsLoading ? (
                    <p className="p-3 text-sm text-muted-foreground">Searching…</p>
                  ) : !isPersonMatchTerm(trimmedSearch) ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      Type at least three letters or digits to search walk-in entrants without
                      linked people.
                    </p>
                  ) : (
                    <ul className="divide-y">
                      {exhibitors
                        .filter(exhibitor => {
                          if (exhibitor.personId || exhibitor.person?.id) return false
                          const haystack = [
                            exhibitor.name,
                            exhibitor.email,
                            exhibitor.phone,
                            exhibitor.entrantCode,
                          ]
                          return haystack.some(
                            value =>
                              typeof value === 'string' &&
                              value.toLowerCase().includes(trimmedSearch.toLowerCase())
                          )
                        })
                        .map(exhibitor => (
                          <li key={exhibitor.id}>
                            <Button
                              variant="ghost"
                              className="w-full justify-start text-left"
                              disabled={personActionPending}
                              onClick={() =>
                                handleEntrantReady(exhibitor, { source: 'existing-entrant' })
                              }
                            >
                              <div>
                                <p className="font-medium">{exhibitor.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {[exhibitor.email, exhibitor.phone, exhibitor.entrantCode]
                                    .filter(Boolean)
                                    .join(' • ') || 'No contact info'}
                                </p>
                              </div>
                            </Button>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
